import React, {
  createRef,
  useState,
  useCallback,
  useEffect,
  Fragment,
  forwardRef,
  Ref,
  ChangeEvent,
  useMemo,
} from 'react';

import clsx from 'clsx';
import { useIntl } from 'react-intl';
import css from 'styled-jsx/css';

import { formatNumberFromInput } from '@utils/formatter';
import { mergeRefs } from '@utils/merge-refs';
import { BackgroundColor } from '@utils/background-colors';

import { Text, Icon } from '@atoms';
import { TooltipWithIcon } from '@molecules/tooltip-with-icon';

import { ColorName, colors } from '@styles';

import { InputProps } from './input.types';

import classes from './input.module.scss';

type Props = InputProps;

type DynamicStylesParams = {
  background?: BackgroundColor;
  border?: ColorName;
};

const getDynamicStyles = ({ background, border }: DynamicStylesParams) => {
  let rules = '';

  if (border) {
    rules += `border: 1px solid ${colors[border]};`;
  }

  if (background) rules += `background: ${colors[background].hex()};`;

  if (rules.length) {
    return css.resolve`
      .${classes.input} {
        ${rules}
      }
    `;
  }
  return { className: '', styles: null };
};

export const Input = forwardRef(
  (
    {
      className,
      label,
      error,
      warning,
      showWarningTooltip,
      touched,
      suffixIcon,
      suffixUnit,
      placeholder,
      round,
      inputComponent: InputComponent,
      background,
      border,
      onChange,
      controlled,
      withoutTouched,
      withoutErrorMessage,
      additionalText,
      suggestions,
      onClear,
      placeholderClass,
      formattedPrice = false,
      layout = 'centered',
      wrapperClass,
      inputClass,
      suffixClass,
      ...nativeInputProps
    }: Props,
    forwardedRef: Ref<HTMLInputElement>,
  ) => {
    const { className: dynamicClassName, styles } = useMemo(
      () => getDynamicStyles({ background, border }),
      [background, border],
    );

    const intl = useIntl();
    const valueRef = createRef<HTMLDivElement>();
    const inputRef = createRef<HTMLInputElement>();
    const [suffixAnimated, toggleSuffixAnimated] = useState(false);
    const pattern = nativeInputProps.type === 'number' ? '[0-9]*' : undefined;
    const inputMode: InputProps['inputMode'] = useMemo(() => {
      if (nativeInputProps.type === 'number' || formattedPrice)
        return 'numeric';
    }, [nativeInputProps.type, formattedPrice]);

    const errorMessage = useMemo(() => {
      if (error && error.message) {
        if (typeof error.message === 'string') return { id: error.message };

        return error.message;
      }
    }, [error]);

    const inputProps = useMemo(() => {
      // input props factory
      const inputProps = {
        className: clsx(
          classes.input,
          dynamicClassName,
          suffixUnit && classes.withSuffix,
          round && classes.round,
          (withoutTouched ? Boolean(errorMessage) : touched) &&
            errorMessage &&
            classes.withError,
          inputClass,
        ),
        ref: mergeRefs(forwardedRef, inputRef),
        ...nativeInputProps,
        pattern,
        inputMode,
      };
      if (controlled === true) {
        inputProps.value =
          typeof nativeInputProps.value === 'undefined'
            ? ''
            : nativeInputProps.value;
      }

      if (controlled === false) {
        inputProps.defaultValue =
          typeof inputProps.defaultValue === 'undefined'
            ? inputProps.value
            : inputProps.defaultValue;
        inputProps.value = undefined;
      }

      if (formattedPrice) {
        inputProps.value =
          formatNumberFromInput(nativeInputProps.value, intl) || '';
      }

      return inputProps;
    }, [
      dynamicClassName,
      suffixUnit,
      round,
      withoutTouched,
      touched,
      errorMessage,
      nativeInputProps,
      pattern,
      controlled,
      inputMode,
      formattedPrice,
      intl,
    ]);

    const onChangeFw = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (formattedPrice) {
          const newVal = e.currentTarget.value.replace(/\D/g, '');
          e.currentTarget.value = newVal || '';
        }

        setInputIsEmpty(e.currentTarget.value === '');

        if (onChange) onChange(e);
      },
      [onChange, formattedPrice],
    );

    /**
     * Suffix effects
     */
    const onSuffixChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        onChangeFw(e);

        e.currentTarget.value === '' && touched
          ? toggleSuffixAnimated(false)
          : toggleSuffixAnimated(true);
      },
      [onChangeFw, touched],
    );

    useEffect(() => {
      if (!suffixUnit) return;
      if (inputProps.value === '' && suffixAnimated) {
        toggleSuffixAnimated(false);
      }
    }, [inputProps.value, suffixAnimated, suffixUnit]);

    useEffect(() => {
      setInputIsEmpty(!nativeInputProps.value);
    }, [nativeInputProps.value]);

    useEffect(() => {
      if (
        (suffixUnit && errorMessage) ||
        (suffixUnit && nativeInputProps.value)
      ) {
        toggleSuffixAnimated(true);
      }
    }, [errorMessage, nativeInputProps.value, suffixUnit]);

    // input component factory
    let input = suffixUnit ? (
      <Fragment key="input">
        <div className={classes.suffixContainer}>
          <span className={classes.suffixValue} ref={valueRef}>
            {formattedPrice
              ? formatNumberFromInput(nativeInputProps.value, intl) || ''
              : nativeInputProps.value}
          </span>
          <span
            className={clsx(
              classes.suffixUnit,
              suffixUnit.id === 'unit.czk' && classes.czk,
              suffixAnimated && classes.animated,
            )}
          >
            <Text
              message={suffixUnit}
              className={clsx(classes.suffixUnitText, suffixClass)}
              margin={0}
              type="reduced"
            />
          </span>
        </div>
        <input {...inputProps} onChange={onSuffixChange} />
      </Fragment>
    ) : (
      <input {...inputProps} onChange={onChangeFw} />
    );

    if (InputComponent) {
      input = (
        <InputComponent.type
          {...inputProps}
          onChange={onChangeFw}
          {...InputComponent.props}
        />
      );
    }

    // error sign utils
    const showErrorSign = useMemo(() => {
      if (withoutTouched) {
        return Boolean(errorMessage);
      }

      return touched && Boolean(errorMessage);
    }, [withoutTouched, touched, errorMessage]);

    const showWarningSign = useMemo(() => {
      if (withoutTouched) {
        return Boolean(warning);
      }

      return touched && Boolean(warning);
    }, [withoutTouched, touched, warning]);

    // clear button utils
    const showClearButton =
      onClear && Boolean(inputProps.value) && !showErrorSign;
    const onClearButtonClick = useCallback(() => {
      if (formattedPrice && inputRef.current) {
        inputRef.current.value = '';
      }
      if (suffixUnit) toggleSuffixAnimated(false);
      if (onClear) onClear();
    }, [suffixUnit, onClear, inputRef, formattedPrice]);

    // touched flag
    const showTouchedFlag =
      !withoutTouched &&
      nativeInputProps.value !== '' &&
      nativeInputProps.value !== nativeInputProps.defaultValue &&
      !errorMessage &&
      !warning;

    const [inputIsEmpty, setInputIsEmpty] = useState(
      !nativeInputProps.value && !nativeInputProps.defaultValue,
    );

    useEffect(() => {
      // otherwise the boolean gets incorrectly determined when
      // you use default values with hook form
      // yes this is a piece of 💩
      // hackerman 420 + 1
      const shouldBeEmpty =
        !inputRef.current?.value && !inputRef.current?.defaultValue;
      if (!shouldBeEmpty) setInputIsEmpty(shouldBeEmpty);
    }, []);

    return (
      <div className={clsx(classes.wrapper, classes[layout], className)}>
        {/* LABEL */}
        {label && (
          <div className={classes.label}>
            <Text
              type={'reduced'}
              message={label}
              textAlign="left"
              color="biscay"
              margin={0}
            />
          </div>
        )}
        <div className={clsx(classes.line, wrapperClass)}>
          {/* INPUT CONTAINER */}
          <div className={clsx(classes.container)}>
            {input}
            {/* PLACEHOLDER */}
            {placeholder && inputIsEmpty && (
              <Text
                type={'reduced'}
                message={placeholder}
                color="heather"
                className={clsx(classes.placeholder, placeholderClass)}
                lineHeight="21px"
              />
            )}
            {/* SUFF ICON */}
            {suffixIcon && <Icon icon={suffixIcon} />}
            {/* CLEAR BUTTON */}
            {showClearButton && (
              <div className={classes.clearButton}>
                <Icon icon={'clear'} onClick={onClearButtonClick} />
              </div>
            )}
          </div>
          {/* ERROR FLAG */}
          {showErrorSign && (
            <span className={clsx(classes.error)}>
              <Icon icon="error-sign" fill={colors.monza.hex()} />
            </span>
          )}
          {showWarningSign && warning && !showErrorSign && (
            <span className={clsx(classes.error)}>
              <TooltipWithIcon
                visible={showWarningTooltip}
                tooltipPlacement="topLeft"
                icon="alert"
                iconColor="lemon_dream"
                titleMessage={warning}
              />
            </span>
          )}
          {/* TOUCHED FLAG */}
          {showTouchedFlag && (
            <span className={classes.touchedIcon}>
              <Icon
                icon="checkmark-circle"
                fill={colors.azure_radiance.hex()}
              />
            </span>
          )}
        </div>

        {/* ERROR MESSAGE */}
        {showErrorSign && errorMessage && !withoutErrorMessage && (
          <Text
            type="small"
            color="monza"
            margin={[9, 0, 0]}
            message={errorMessage}
          />
        )}

        {/* ADDITIONAL TEXT */}
        {additionalText && (
          <Text
            type="small"
            color="slate_gray"
            margin={[9, 0, 0]}
            message={additionalText}
          />
        )}

        {/* SUGGESTIONS */}
        {suggestions && (
          <div className={classes.suggestions}>{suggestions}</div>
        )}
        {styles}
      </div>
    );
  },
);
